import { AxiosError } from "axios";
export const FORM_ERROR_TYPES = {
    MANUAL: "manual",
    VALIDATION: "validation",
    AUTH: "auth",
    PERMISSION: "permission",
    CONFLICT: "conflict",
    RATE_LIMIT: "rateLimit",
    SERVER: "server",
    NETWORK: "network",
    TIMEOUT: "timeout",
    UNKNOWN: "unknown"
};
export const useFormError = ()=>{
    return (error, setError)=>{
        if (error instanceof AxiosError) {
            const status = error.response?.status;
            const data = error.response?.data;
            // Handle field-level validation errors (400, 422)
            if (status === 400 || status === 422) {
                // Check for field-specific errors
                const fieldErrors = data?.fields || data?.errors;
                if (fieldErrors) {
                    // Map backend field errors to form fields
                    Object.entries(fieldErrors).forEach(([field, message])=>{
                        // Handle both string and array of strings
                        const errorMessage = Array.isArray(message) ? message[0] : message;
                        try {
                            setError(field, {
                                type: FORM_ERROR_TYPES.MANUAL,
                                message: errorMessage
                            });
                        } catch  {
                            // If field doesn't exist in form, add to root
                            setError("root", {
                                type: FORM_ERROR_TYPES.MANUAL,
                                message: `${field}: ${errorMessage}`
                            });
                        }
                    });
                } else if (data?.message) {
                    // General validation error without field mapping
                    setError("root", {
                        type: FORM_ERROR_TYPES.VALIDATION,
                        message: data.message
                    });
                } else {
                    setError("root", {
                        type: FORM_ERROR_TYPES.VALIDATION,
                        message: "Validation failed. Please check your input."
                    });
                }
            } else if (status === 401) {
                setError("root", {
                    type: FORM_ERROR_TYPES.AUTH,
                    message: data?.message || "Authentication required. Please log in."
                });
            } else if (status === 403) {
                setError("root", {
                    type: FORM_ERROR_TYPES.PERMISSION,
                    message: data?.message || "You don't have permission to perform this action."
                });
            } else if (status === 409) {
                setError("root", {
                    type: FORM_ERROR_TYPES.CONFLICT,
                    message: data?.message || "This item already exists."
                });
            } else if (status === 429) {
                setError("root", {
                    type: FORM_ERROR_TYPES.RATE_LIMIT,
                    message: data?.message || "Too many requests. Please try again later."
                });
            } else if (status && status >= 500) {
                setError("root", {
                    type: FORM_ERROR_TYPES.SERVER,
                    message: "A server error occurred. Please try again later."
                });
            } else if (error.code === "ERR_NETWORK") {
                setError("root", {
                    type: FORM_ERROR_TYPES.NETWORK,
                    message: "Network error. Please check your connection."
                });
            } else if (error.code === "ECONNABORTED") {
                setError("root", {
                    type: FORM_ERROR_TYPES.TIMEOUT,
                    message: "Request timed out. Please try again."
                });
            } else {
                setError("root", {
                    type: FORM_ERROR_TYPES.UNKNOWN,
                    message: data?.message || error.message || "An unexpected error occurred."
                });
            }
        } else if (error instanceof Error) {
            setError("root", {
                type: FORM_ERROR_TYPES.UNKNOWN,
                message: error.message
            });
        } else {
            setError("root", {
                type: FORM_ERROR_TYPES.UNKNOWN,
                message: "An unexpected error occurred. Please try again."
            });
        }
    };
};
function extractErrorMessage(errorValue) {
    if (typeof errorValue !== "object" || errorValue === null || !("message" in errorValue)) {
        return undefined;
    }
    const message = errorValue.message;
    return typeof message === "string" ? message : undefined;
}
// Helper to extract the first error message from form errors
export const getFirstErrorMessage = (errors)=>{
    const rootMessage = extractErrorMessage(errors["root"]);
    if (rootMessage) {
        return rootMessage;
    }
    for (const errorValue of Object.values(errors)){
        const message = extractErrorMessage(errorValue);
        if (message) {
            return message;
        }
    }
    return undefined;
};
